import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Settings, AlertCircle, CheckCircle, Edit, Trash2 } from 'lucide-react';
import { useFieldValidationConfigs, CreateFieldConfigData, UpdateFieldConfigData } from '@/hooks/useFieldValidationConfigs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

export const MandatoryFieldValidator: React.FC = () => {
  const { 
    configs, 
    mandatoryConfigs, 
    configsByGroup, 
    isLoading, 
    createConfig, 
    updateConfig, 
    deleteConfig, 
    toggleMandatory,
    isCreating, 
    isUpdating, 
    isDeleting 
  } = useFieldValidationConfigs();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<any>(null);
  const [formData, setFormData] = useState<CreateFieldConfigData>({
    field_name: '',
    display_name: '',
    is_mandatory: false,
    field_type: 'text',
    field_group: 'general',
    sort_order: 0,
    help_text: ''
  });

  const fieldTypeOptions = [
    { value: 'text', label: 'Text' },
    { value: 'number', label: 'Number' },
    { value: 'date', label: 'Date' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'currency', label: 'Currency' },
    { value: 'percentage', label: 'Percentage' },
    { value: 'boolean', label: 'Yes/No' }
  ];

  const fieldGroupOptions = [
    { value: 'general', label: 'General Information' },
    { value: 'financial', label: 'Financial Details' },
    { value: 'supplier', label: 'Supplier Information' },
    { value: 'customer', label: 'Customer Information' },
    { value: 'technical', label: 'Technical Details' }
  ];

  const resetForm = () => {
    setFormData({
      field_name: '',
      display_name: '',
      is_mandatory: false,
      field_type: 'text',
      field_group: 'general',
      sort_order: 0,
      help_text: ''
    });
    setEditingConfig(null);
  };

  const handleSubmit = () => {
    if (editingConfig) {
      updateConfig(editingConfig.id, formData as UpdateFieldConfigData);
      setIsEditDialogOpen(false);
    } else {
      createConfig(formData);
      setIsCreateDialogOpen(false);
    }
    resetForm();
  };

  const handleEdit = (config: any) => {
    setEditingConfig(config);
    setFormData({
      field_name: config.field_name,
      display_name: config.display_name,
      is_mandatory: config.is_mandatory,
      field_type: config.field_type,
      field_group: config.field_group,
      sort_order: config.sort_order,
      help_text: config.help_text || ''
    });
    setIsEditDialogOpen(true);
  };

  const getFieldTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      text: 'bg-blue-100 text-blue-800',
      number: 'bg-green-100 text-green-800',
      date: 'bg-purple-100 text-purple-800',
      email: 'bg-orange-100 text-orange-800',
      phone: 'bg-indigo-100 text-indigo-800',
      currency: 'bg-emerald-100 text-emerald-800',
      percentage: 'bg-yellow-100 text-yellow-800',
      boolean: 'bg-gray-100 text-gray-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getGroupIcon = (group: string) => {
    switch (group) {
      case 'financial': return '💰';
      case 'supplier': return '🏢';
      case 'customer': return '👤';
      case 'technical': return '⚙️';
      default: return '📋';
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-muted animate-pulse rounded" />
        <div className="h-64 bg-muted animate-pulse rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Mandatory Field Configuration</h2>
          <p className="text-muted-foreground">
            Configure which fields are required for bill processing validation
          </p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Field
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add New Field Configuration</DialogTitle>
              <DialogDescription>
                Configure a new field for validation requirements
              </DialogDescription>
            </DialogHeader>
            <FieldConfigForm 
              formData={formData}
              setFormData={setFormData}
              onSubmit={handleSubmit}
              onCancel={() => {
                setIsCreateDialogOpen(false);
                resetForm();
              }}
              isSubmitting={isCreating}
              fieldTypeOptions={fieldTypeOptions}
              fieldGroupOptions={fieldGroupOptions}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Fields</CardTitle>
            <Settings className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{configs.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Mandatory Fields</CardTitle>
            <AlertCircle className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{mandatoryConfigs.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Rules</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {configs.filter(c => c.is_active).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Field Groups</CardTitle>
            <div className="h-4 w-4 text-muted-foreground">📊</div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{Object.keys(configsByGroup).length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Field Groups */}
      <div className="space-y-6">
        {Object.entries(configsByGroup).map(([groupName, groupConfigs]) => (
          <Card key={groupName}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="text-xl">{getGroupIcon(groupName)}</span>
                {fieldGroupOptions.find(g => g.value === groupName)?.label || groupName}
                <Badge variant="secondary">{groupConfigs.length} fields</Badge>
              </CardTitle>
              <CardDescription>
                Field configurations for {groupName} information
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {groupConfigs.map((config) => (
                  <div
                    key={config.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <Switch
                          checked={config.is_mandatory}
                          onCheckedChange={(checked) => toggleMandatory(config.id, checked)}
                          disabled={isUpdating}
                        />
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{config.display_name}</span>
                            <Badge className={getFieldTypeColor(config.field_type)}>
                              {config.field_type}
                            </Badge>
                            {config.is_mandatory && (
                              <Badge variant="destructive">Required</Badge>
                            )}
                            {!config.is_active && (
                              <Badge variant="outline">Inactive</Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Field: {config.field_name}
                            {config.help_text && ` • ${config.help_text}`}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(config)}
                        disabled={isUpdating}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteConfig(config.id)}
                        disabled={isDeleting}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {groupConfigs.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No fields configured for this group
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Field Configuration</DialogTitle>
            <DialogDescription>
              Modify the field validation configuration
            </DialogDescription>
          </DialogHeader>
          <FieldConfigForm 
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
            onCancel={() => {
              setIsEditDialogOpen(false);
              resetForm();
            }}
            isSubmitting={isUpdating}
            fieldTypeOptions={fieldTypeOptions}
            fieldGroupOptions={fieldGroupOptions}
            isEditing={true}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

interface FieldConfigFormProps {
  formData: CreateFieldConfigData;
  setFormData: (data: CreateFieldConfigData) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isSubmitting: boolean;
  fieldTypeOptions: { value: string; label: string }[];
  fieldGroupOptions: { value: string; label: string }[];
  isEditing?: boolean;
}

const FieldConfigForm: React.FC<FieldConfigFormProps> = ({
  formData,
  setFormData,
  onSubmit,
  onCancel,
  isSubmitting,
  fieldTypeOptions,
  fieldGroupOptions,
  isEditing = false
}) => {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="field_name">Field Name</Label>
          <Input
            id="field_name"
            value={formData.field_name}
            onChange={(e) => setFormData({ ...formData, field_name: e.target.value })}
            placeholder="e.g., bill_amount"
            disabled={isEditing}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="display_name">Display Name</Label>
          <Input
            id="display_name"
            value={formData.display_name}
            onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
            placeholder="e.g., Bill Amount"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="field_type">Field Type</Label>
          <Select
            value={formData.field_type}
            onValueChange={(value: any) => setFormData({ ...formData, field_type: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {fieldTypeOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="field_group">Field Group</Label>
          <Select
            value={formData.field_group}
            onValueChange={(value) => setFormData({ ...formData, field_group: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {fieldGroupOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="help_text">Help Text</Label>
        <Textarea
          id="help_text"
          value={formData.help_text}
          onChange={(e) => setFormData({ ...formData, help_text: e.target.value })}
          placeholder="Optional help text for this field"
          rows={2}
        />
      </div>

      <div className="flex items-center space-x-2">
        <Switch
          checked={formData.is_mandatory}
          onCheckedChange={(checked) => setFormData({ ...formData, is_mandatory: checked })}
        />
        <Label>Make this field mandatory</Label>
      </div>

      <Separator />

      <div className="flex justify-end space-x-2">
        <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={onSubmit} disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : isEditing ? 'Update Field' : 'Create Field'}
        </Button>
      </div>
    </div>
  );
};